import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card } from '../ui/Card';
import { Avatar } from '../ui/Avatar';
import { sellers } from '../../utils/mockData';
interface EvolutionChartProps {
  period: string;
}
const COLORS = ['#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6' // blue
];
// Custom dot component for the chart
const CustomDot = (props: any) => {
  const {
    cx,
    cy,
    payload,
    dataKey,
    index
  } = props;
  // Only show dot every 5th data point to avoid clutter
  if (index % 5 !== 0 && index !== 29 && index !== 89) return null;
  // Find the seller ID from the dataKey (e.g., "seller_1")
  const sellerId = dataKey.split('_')[1];
  const seller = sellers.find(s => s.id === sellerId);
  if (!seller) return null;
  return <foreignObject x={cx - 10} y={cy - 10} width={20} height={20} data-id="element-112">
      <div className="h-5 w-5 rounded-full overflow-hidden border-2 border-white shadow-lg hover:scale-150 transition-transform cursor-pointer" data-id="element-113">
        <img src={seller.avatar} alt={seller.name} className="h-full w-full object-cover" data-id="element-114" />
      </div>
    </foreignObject>;
};
export function EvolutionChart({
  period
}: EvolutionChartProps) {
  // Get top 5 sellers only
  const topSellers = [...sellers].sort((a, b) => b.salesCount - a.salesCount).slice(0, 5);
  // Generate realistic cumulative data
  const generateData = () => {
    const days = period === 'Mensal' ? 30 : period === 'Trimestral' ? 90 : 365;
    const data = [];
    // Initialize current revenue for each seller
    const currentRevenue: Record<string, number> = {};
    topSellers.forEach(s => currentRevenue[`seller_${s.id}`] = 0);
    for (let i = 1; i <= days; i++) {
      const dayData: any = {
        day: i
      };
      topSellers.forEach(seller => {
        // Add random daily sales (some days 0, some high)
        const dailySale = Math.random() > 0.3 ? Math.random() * 5000 : 0;
        // Add a trend factor based on their total revenue (better sellers sell more consistently)
        const performanceFactor = seller.revenue / 100000;
        currentRevenue[`seller_${seller.id}`] += dailySale * performanceFactor;
        dayData[`seller_${seller.id}`] = Math.round(currentRevenue[`seller_${seller.id}`]);
      });
      data.push(dayData);
    }
    return data;
  };
  const data = generateData();
  return <Card className="p-6" data-id="element-115">
      <div className="flex justify-between items-center mb-6" data-id="element-116">
        <div data-id="element-117">
          <h3 className="text-lg font-bold text-white" data-id="element-118">
            Evolução de Vendas - Top 5
          </h3>
          <p className="text-sm text-text-muted" data-id="element-119">
            Desempenho acumulado dos melhores vendedores
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-120">
          <span className="text-sm text-text-muted" data-id="element-121">Período:</span>
          <span className="text-sm font-medium text-white bg-slate-800 px-3 py-1 rounded-lg border border-slate-700" data-id="element-122">
            {period}
          </span>
        </div>
      </div>

      <div className="h-[400px] w-full" data-id="element-123">
        <ResponsiveContainer width="100%" height="100%" data-id="element-124">
          <LineChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 10
        }} data-id="element-125">
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} data-id="element-126" />
            <XAxis dataKey="day" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} label={{
            value: 'Dia do Período',
            position: 'insideBottom',
            offset: -5,
            fill: '#94A3B8',
            fontSize: 12
          }} data-id="element-127" />
            <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `R$${value / 1000}k`} data-id="element-128" />
            <Tooltip contentStyle={{
            backgroundColor: '#1E293B',
            border: '1px solid #334155',
            borderRadius: '8px'
          }} itemStyle={{
            fontSize: '12px'
          }} labelStyle={{
            color: '#F1F5F9',
            fontWeight: 'bold',
            marginBottom: '8px'
          }} formatter={(value: number, name: string) => {
            const sellerId = name.split('_')[1];
            const seller = topSellers.find(s => s.id === sellerId);
            return [`R$ ${value.toLocaleString()}`, seller?.name || name];
          }} labelFormatter={label => `Dia ${label}`} data-id="element-129" />
            <Legend content={({
            payload
          }) => <div className="flex flex-wrap justify-center gap-4 mt-4" data-id="element-131">
                  {payload?.map((entry: any, index: number) => {
              const sellerId = entry.dataKey.split('_')[1];
              const seller = topSellers.find(s => s.id === sellerId);
              if (!seller) return null;
              return <div key={index} className="flex items-center gap-2" data-id="element-132">
                        <div className="h-3 w-3 rounded-full" style={{
                  backgroundColor: entry.color
                }} data-id="element-133" />
                        <Avatar src={seller.avatar} fallback={seller.name} size="xs" className="h-5 w-5 text-[8px]" data-id="element-134" />
                        <span className="text-sm text-text-secondary font-medium" data-id="element-135">
                          {seller.name}
                        </span>
                      </div>;
            })}
                </div>} data-id="element-130" />
            {topSellers.map((seller, index) => <Line key={seller.id} type="monotone" dataKey={`seller_${seller.id}`} stroke={COLORS[index]} strokeWidth={3} dot={<CustomDot data-id="element-137" />} activeDot={{
            r: 6,
            strokeWidth: 0
          }} animationDuration={2000} data-id="element-136" />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>;
}